import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { clearSession, getToken } from "./session";

const baseUrl = import.meta.env.VITE_API_BASE_URL;

const AUTH_PATHS = ["/auth/login", "/auth/login/select", "/auth/password", "/auth/logout"];

type ReloginResult = "renewed" | "unavailable" | "deferred" | "rejected" | "failed";

let onUnauthorized: (() => void) | null = null;
let onForbidden: (() => void) | null = null;
let relogin: (() => Promise<ReloginResult>) | null = null;
let refreshingProfile = false;

const sentRequests = new WeakMap<Request, { original: Request; token: string | null }>();

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export function setReloginHandler(handler: (() => Promise<ReloginResult>) | null): void {
  relogin = handler;
}

export function setForbiddenHandler(handler: () => Promise<unknown> | unknown): void {
  onForbidden = () => {
    if (refreshingProfile) {
      return;
    }
    refreshingProfile = true;
    Promise.resolve(handler()).finally(() => {
      refreshingProfile = false;
    });
  };
}

function expireSession(): void {
  clearSession();
  onUnauthorized?.();
}

function handleResponseStatus(status: number, url: string): void {
  if (status === 401) {
    expireSession();
    return;
  }
  if (status === 403 && !url.endsWith("/auth/me")) {
    onForbidden?.();
  }
}

function isAuthPath(url: string): boolean {
  const path = new URL(url, "http://localhost").pathname;
  return AUTH_PATHS.some((authPath) => path.endsWith(authPath));
}

async function retryWithFreshToken(request: Request): Promise<Response | null> {
  const sent = sentRequests.get(request);
  const token = getToken();
  if (!sent || !token) {
    return null;
  }
  const retried = sent.original.clone();
  retried.headers.set("Authorization", `Bearer ${token}`);
  try {
    const response = await globalThis.fetch(retried);
    handleResponseStatus(response.status, retried.url);
    return response;
  } catch {
    return null;
  }
}

async function handleUnauthorized(request: Request, response: Response): Promise<Response> {
  if (isAuthPath(request.url)) {
    expireSession();
    return response;
  }
  const sent = sentRequests.get(request);
  if (sent && sent.token !== getToken()) {
    return (await retryWithFreshToken(request)) ?? response;
  }
  const result = relogin ? await relogin() : "unavailable";
  if (result === "failed" || result === "deferred") {
    return response;
  }
  if (result !== "renewed") {
    expireSession();
    return response;
  }
  return (await retryWithFreshToken(request)) ?? response;
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = getToken();
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    sentRequests.set(request, { original: request.clone(), token });
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status === 401) {
      return handleUnauthorized(request, response);
    }
    handleResponseStatus(response.status, request.url);
    return response;
  },
};

export const api = createClient<paths>({
  baseUrl,
  fetch: (request) => globalThis.fetch(request),
});
api.use(authMiddleware);
